import React from "react";
import { QRCodeCanvas } from "qrcode.react";
import { useTranslation } from "react-i18next";
import ButtonHapticWrapper from "component/Button/ButtonHapticWrapper";

type InviteSheetProps = {
    isOpen: boolean;
    onClose: () => void;
    lobbyUrl: string;
    lobbyCode: string;
    copiedLink: boolean;
    copiedCode: boolean;
    onCopyLink: () => void;
    onCopyCode: () => void;
};

const InviteSheet = ({ isOpen, onClose, lobbyUrl, lobbyCode, copiedLink, copiedCode, onCopyLink, onCopyCode }: InviteSheetProps) => {
    const { t } = useTranslation();
    const [isClosing, setIsClosing] = React.useState(false);

    const handleClose = React.useCallback(() => {
        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            onClose();
        }, 300);
    }, [onClose]);

    if(!isOpen) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
            <div
                className={`absolute inset-0 bg-black/50 ${isClosing ? "animate-backdrop-out" : "animate-backdrop-in"}`}
                onClick={handleClose}
            ></div>

            <div
                className={`relative rounded-t-2xl px-5 pt-3 pb-8 ${isClosing ? "animate-sheet-down" : "animate-sheet-up"}`}
                style={{ background: "linear-gradient(180deg, #2570D9 0%, #1A5BC4 100%)", borderTop: "1px solid rgba(255,255,255,0.15)" }}
            >
                {/* Handle */}
                <div className="w-8 h-0.5 rounded-full bg-white/20 mx-auto mb-5"></div>

                {/* QR + Room code side by side */}
                <div className="flex items-center gap-4 mb-5">
                    <div className="bg-white rounded-xl p-2.5 shadow-lg shrink-0">
                        <QRCodeCanvas value={lobbyUrl} size={112} level="L" />
                    </div>

                    <div className="flex-1 flex flex-col gap-1.5">
                        <p className="text-white/40 text-[9px] font-semibold uppercase tracking-widest">{t("lobby.room_code")}</p>
                        <ButtonHapticWrapper>
                            <button onClick={onCopyCode} className="relative flex items-center gap-2 cursor-pointer group">
                                <span className="text-white font-extrabold text-xl tracking-[0.15em]">{lobbyCode}</span>
                                {copiedCode ? (
                                    <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                ) : (
                                    <svg className="w-4 h-4 text-white/20 group-hover:text-white/40 transition-colors" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <rect x="9" y="9" width="13" height="13" rx="2" />
                                        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                                    </svg>
                                )}
                            </button>
                        </ButtonHapticWrapper>
                        <p className="text-white/30 text-[10px] leading-tight">{t("lobby.scan_or_share")}</p>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                    <ButtonHapticWrapper>
                        <button onClick={onCopyLink} className="flex-1 flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 transition-colors rounded-xl py-3 cursor-pointer border border-white/10">
                            {copiedLink ? (
                                <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                            ) : (
                                <svg className="w-4 h-4 text-white/50" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                            )}
                            <span className="text-white/50 text-xs font-semibold">{copiedLink ? t("lobby.link_copied") : t("lobby.copy_link")}</span>
                        </button>
                    </ButtonHapticWrapper>

                    <ButtonHapticWrapper>
                        <button onClick={() => { if(navigator.share) { navigator.share({ url: lobbyUrl }); } }} className="flex-1 flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 transition-colors rounded-xl py-3 cursor-pointer border border-white/10">
                            <svg className="w-4 h-4 text-white/50" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" /></svg>
                            <span className="text-white/50 text-xs font-semibold">{t("lobby.share")}</span>
                        </button>
                    </ButtonHapticWrapper>
                </div>
            </div>
        </div>
    );
};

export default InviteSheet;
